import { IPagination } from "@fl/shared/src/types/pagination";
import {
  ITableDataAction,
  ITableDataColumns,
} from "@fl/shared/src/types/tables";
import moment from "moment";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import { HiSortAscending, HiSortDescending } from "react-icons/hi";
import PaginationComp from "../pagination";
import "./styles.css";

interface IProps {
  columns: ITableDataColumns[];
  data: any[];
  striped?: boolean;
  bordered?: boolean;
  hover?: boolean;
  showIndex?: boolean;
  textSizeHeader?: number;
  textSizeData?: number;
  hoverSmooth?: boolean;
  pagination?: IPagination; //melhoria
}

const TableData: React.FC<IProps> = ({
  striped = true,
  bordered = true,
  hover = true,
  columns,
  data,
  showIndex = false,
  textSizeHeader = 14,
  textSizeData = 14,
  hoverSmooth = true,
  pagination,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const [hoverStates, setHoverStates] = useState<Record<string, boolean>>({});

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return data;

    const sorted = [...data];
    sorted.sort((a, b) => {
      const column = columns.find((c) => c.columnKey === sortConfig.key);

      if (!column) return 0; // Se não encontrar coluna, não ordena

      const aValue = column.sortValue ? column.sortValue(a) : a[sortConfig.key];
      const bValue = column.sortValue ? column.sortValue(b) : b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortConfig, columns]);

  const handleSort = (key: string) => {
    if (sortConfig?.key === key) {
      setSortConfig({
        key,
        direction: sortConfig.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSortConfig({ key, direction: "asc" });
    }
  };

  const handleHover = (actionKey: string, isHovering: boolean) => {
    setHoverStates((prev) => ({ ...prev, [actionKey]: isHovering }));
  };

  const renderActions = (
    actions: ITableDataAction[] | ((item: any) => ITableDataAction[]),
    rowKey: string,
    item: any
  ) => {
    const resolvedActions =
      typeof actions === "function" ? actions(item) : actions;

    return resolvedActions.map((action, index) => {
      const actionKey = `${rowKey}_action_${index}`;
      const isHovering = hoverStates[actionKey] || false;

      return (
        <div
          key={actionKey}
          className={`pointer ${actions.length > 1 ? "icons" : ""}`}
          onClick={() => action.action(item)}
          onMouseOver={() => handleHover(actionKey, true)}
          onMouseLeave={() => handleHover(actionKey, false)}
          title={action.label}
        >
          {isHovering ? action.iconHover : action.icon}
        </div>
      );
    });
  };

  const renderRows = () => {
    const columnKey = columns.find((c) => c.pk)?.columnKey;

    return sortedData.map((item, rowIndex) => {
      // Gera um identificador único usando rowIndex e o valor do columnKey
      const rowKey = columnKey
        ? `${item[columnKey]}_${rowIndex}`
        : `row_${rowIndex}`;

      return (
        <tr key={rowKey} className={hoverSmooth ? "hover-bold-smooth" : ""}>
          {showIndex && (
            <td className="text-center" style={{ verticalAlign: "middle" }}>
              {rowIndex + 1}
            </td>
          )}
          {columns.map((column) => {
            if (column.showColumn === undefined || column.showColumn === true) {
              if (column.action && column.actionButtons) {
                return (
                  <td
                    key={`${rowKey}_${column.columnKey}`}
                    className={
                      column.alignText ? column.alignText : "text-start"
                    }
                    style={
                      column.alignVert
                        ? { verticalAlign: column.alignVert }
                        : { verticalAlign: "middle" }
                    }
                  >
                    <div
                      className={
                        column.actionColumnIcons
                          ? "icons-container-column"
                          : "icons-container-row"
                      }
                    >
                      {renderActions(column.actionButtons, rowKey, item)}
                    </div>
                  </td>
                );
              } else {
                // Se houver função render, use-a. Caso contrário use o comportamento padrão
                const value = column.render
                  ? column.render(item)
                  : column.dateColumns
                    ? moment(item[column.columnKey]).format(
                        "DD/MM/YYYY [às] HH:mm:ss"
                      )
                    : item[column.columnKey];

                return (
                  <td
                    key={`${rowKey}_${column.columnKey}`}
                    className={column.alignText}
                    style={
                      column.alignVert
                        ? { verticalAlign: column.alignVert }
                        : { verticalAlign: "middle" }
                    }
                  >
                    {value}
                  </td>
                );
              }
            }
          })}
        </tr>
      );
    });
  };

  return (
    <div>
      <Table
        className="my-3"
        striped={striped}
        bordered={bordered}
        hover={hover}
      >
        <thead>
          <tr
            className="disable-selection"
            style={{ verticalAlign: "middle", fontSize: `${textSizeHeader}px` }}
          >
            {showIndex && <th className="text-center">#</th>}
            {columns.map((column, index) => {
              if (
                column.showColumn === undefined ||
                column.showColumn === true
              ) {
                return (
                  <th
                    key={`header_${index}`}
                    className={`text-center ${column.action || column.disableSort ? "" : "pointer clickable"}`}
                    onClick={() =>
                      !column.action &&
                      !column.disableSort &&
                      handleSort(column.columnKey)
                    }
                  >
                    {column.label}{" "}
                    {!column.action
                      ? sortConfig?.key === column.columnKey &&
                        (sortConfig.direction === "asc" ? (
                          <HiSortAscending />
                        ) : (
                          <HiSortDescending />
                        ))
                      : null}
                  </th>
                );
              }
            })}
          </tr>
        </thead>
        <tbody style={{ fontSize: `${textSizeData}px` }}>{renderRows()}</tbody>
      </Table>

      {pagination && <PaginationComp {...pagination} />}
    </div>
  );
};

export default TableData;
